import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon, HelpCircleIcon } from 'lucide-react';
interface FaqItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  delay: number;
  isInView: boolean;
}
function FaqItem({
  question,
  answer,
  isOpen,
  onClick,
  delay,
  isInView
}: FaqItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay
  }} className="border-b border-gray-200 last:border-0" data-id="element-48">
      <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left" aria-expanded={isOpen} data-id="element-49">
        <span className="text-lg font-semibold text-[#1a2744] pr-4" data-id="element-50">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-[#c9a227] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-51" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-52">
        <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-53">{answer}</p>
      </motion.div>
    </motion.div>;
}
export function FaqSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: '¿Esta guía es legal? ¿No voy a tener problemas con el IRS?',
    answer: 'Absolutamente legal. El Escudo 1099 solo enseña deducciones que están permitidas por el código fiscal de Estados Unidos. No hay trucos ni zonas grises — solo estrategias que los contadores profesionales usan todos los días para sus clientes más ricos.'
  }, {
    question: '¿Funciona si trabajo para Uber, DoorDash, o en construcción?',
    answer: 'Sí. La guía está diseñada específicamente para trabajadores 1099 en cualquier industria: delivery, rideshare, construcción, limpieza, jardinería, barbería, y más. Incluye ejemplos específicos para cada tipo de trabajo.'
  }, {
    question: '¿Necesito saber inglés para usar la guía?',
    answer: 'No. El Escudo 1099 está 100% en español, escrito en lenguaje simple y claro. Nada de jerga legal complicada.'
  }, {
    question: '¿Cuándo recibiré la guía?',
    answer: 'Inmediatamente. Después de tu compra, recibirás un enlace de descarga instantánea. Puedes empezar a leer en los próximos 5 minutos.'
  }, {
    question: '¿Puedo usar esta guía si ya tengo un preparador de impuestos?',
    answer: '¡Claro que sí! De hecho, es mejor. Llegarás a tu cita con tu preparador sabiendo exactamente qué deducciones pedir. Muchos clientes reportan que sus preparadores quedaron impresionados con su nivel de preparación.'
  }];
  return <section ref={ref} className="bg-gray-50 py-20 lg:py-28" data-id="element-54">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-55">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-56">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a2744] text-white rounded-full text-sm font-semibold mb-6" data-id="element-57">
            <HelpCircleIcon className="w-4 h-4" data-id="element-58" />
            Preguntas Frecuentes
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] leading-tight" data-id="element-59">
            ¿Tienes <span className="text-[#c9a227]" data-id="element-60">Preguntas?</span>
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-white rounded-2xl shadow-lg p-6 lg:p-8" data-id="element-61">
          {faqs.map((faq, index) => <FaqItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} delay={0.1 + index * 0.05} isInView={isInView} data-id="element-62" />)}
        </motion.div>
      </div>
    </section>;
}